import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from '../components/layout';
import {
  Dashboard, NewQuote, QuoteHistory, QuoteDetailPage, Clients, Login, Validation,
  CarrierDashboard, CarrierUserDashboard, CarrierUsers,
} from '../pages';
import {
  getAuthUser, homeRouteFor, isCarrierAdmin, isCarrierSubUser, isNowCertsUser,
} from '../services/authApi';

/** Signed in at all? The API's [Authorize] is the real gate; this only routes. */
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true';
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

/**
 * Keep a user out of a carrier dashboard that is not theirs. Applied only to the
 * new carrier routes — the existing NowCerts routes are left exactly as they were.
 */
const RoleRoute: React.FC<{
  allow: (u: ReturnType<typeof getAuthUser>) => boolean;
  children: React.ReactElement;
}> = ({ allow, children }) => {
  const user = getAuthUser();
  if (!user) return <Navigate to="/login" replace />;
  return allow(user) ? children : <Navigate to={homeRouteFor(user)} replace />;
};

const HomeRedirect: React.FC = () => (
  <Navigate to={homeRouteFor(getAuthUser())} replace />
);

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/truckingvalidation" element={<Validation />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeRedirect />} />

        {/* Agency-scoped pages. /api/account/quotelist filters on the AgencyId
            claim, which is empty on a carrier token — so these stay NowCerts-only
            or a carrier sees every agency's quotes. */}
        <Route
          path="dashboard"
          element={<RoleRoute allow={isNowCertsUser}><Dashboard /></RoleRoute>}
        />
        <Route
          path="new-quote"
          element={<RoleRoute allow={isNowCertsUser}><NewQuote /></RoleRoute>}
        />
        <Route
          path="quote-history"
          element={<RoleRoute allow={isNowCertsUser}><QuoteHistory /></RoleRoute>}
        />
        <Route
          path="quote-history/:id"
          element={<RoleRoute allow={isNowCertsUser}><QuoteDetailPage /></RoleRoute>}
        />
        <Route
          path="clients"
          element={<RoleRoute allow={isNowCertsUser}><Clients /></RoleRoute>}
        />

        {/* ---- New: carrier only ---- */}
        <Route
          path="carrier"
          element={
            <RoleRoute allow={isCarrierAdmin}>
              <CarrierDashboard />
            </RoleRoute>
          }
        />
        <Route
          path="carrier/quote/:id"
          element={
            <RoleRoute allow={isCarrierAdmin}>
              <QuoteDetailPage />
            </RoleRoute>
          }
        />
        <Route
          path="carrier/users"
          element={
            <RoleRoute allow={isCarrierAdmin}>
              <CarrierUsers />
            </RoleRoute>
          }
        />
        <Route
          path="my-quotes/quote/:id"
          element={
            <RoleRoute allow={isCarrierSubUser}>
              <QuoteDetailPage />
            </RoleRoute>
          }
        />
        <Route
          path="my-quotes"
          element={
            <RoleRoute allow={isCarrierSubUser}>
              <CarrierUserDashboard />
            </RoleRoute>
          }
        />

      </Route>
    </Routes>
  );
};

export default AppRoutes;
